/**
 * «Armar en el oficial» con borradores de esa segmentación (29/10/2026), antes de «A quién»: el operador arma una campaña
 * del oficial por grupo, cada una con su plantilla y su cupón, así que no se abre ninguno solo. Cada borrador dice a qué
 * grupos va y su plantilla (o que falta), con «Seguir armando»; «Armar otra» sigue a «A quién» de siempre, donde el
 * borrador nace al tocar «Siguiente» (`useBorradorDelAnalisis`).
 *
 * Los que dejó «Preparar el envío» (una campaña por plantilla de Mensajes, R3) también están acá, cada uno con la suya.
 * «Armar otra» con grupos que ya están en otra campaña no les escribe dos veces: el chequeo lo frena si esa otra salió
 * (`GRUPOS_EN_OTRA_CAMPANA`) y lo avisa si es un borrador.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import type { AudienciaActiva, CampanaResumen } from "../../types";
import { gruposDeLaCampana } from "../../logic/asistente";
import { rutaAsistenteCampana } from "../AccionesCampana";

export function ElegirDelAnalisis({ borradores, audiencias, onArmarOtra }: {
  borradores: CampanaResumen[];
  /** las segmentaciones que carga el asistente: los nombres de los grupos (null mientras llegan: se dice cuántos son) */
  audiencias: AudienciaActiva[] | null;
  onArmarOtra: () => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <Link to="/activa/oficial/campanas" className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
          <ArrowLeft size={13} /> Campañas
        </Link>
        <h2 className="text-xl font-bold text-slate-900 tracking-tight mt-1">Nueva campaña</h2>
      </div>

      <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-4" aria-labelledby="elegir-del-analisis" data-testid="elegir-del-analisis">
        <h3 id="elegir-del-analisis" className="text-lg font-semibold text-slate-900 tracking-tight">Ya tenés campañas del oficial armándose con esta segmentación</h3>
        <ul className="space-y-2">
          {borradores.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-100 p-4">
              <span className="min-w-0 flex-[1_1_220px]">
                <span className="block text-sm font-semibold text-slate-900">{c.nombre}</span>
                <span className="block text-xs text-slate-500">
                  Para {gruposDeLaCampana(c, audiencias)} · {c.plantilla ? `plantilla «${c.plantilla.nombreVisible}»` : "falta la plantilla"}
                </span>
              </span>
              <Link to={rutaAsistenteCampana(c.id)} replace className="inline-flex h-11 items-center rounded-xl bg-violet-600 px-4 text-sm font-semibold text-white hover:bg-violet-700">
                Seguir armando
              </Link>
            </li>
          ))}
        </ul>
        <div className="border-t border-slate-100 pt-4">
          <button type="button" onClick={onArmarOtra} className="inline-flex h-11 items-center gap-1.5 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <Plus size={15} aria-hidden="true" /> Armar otra
          </button>
          <p className="mt-1 text-xs text-slate-500">Para otros grupos de esta segmentación</p>
        </div>
      </section>
    </div>
  );
}
